import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CardActionArea from "@mui/material/CardActionArea";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";

import { del, get, post, put } from "lib/api";
import StatusChip from "components/StatusChip";
import InfoCard from "mobile/components/InfoCard";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosCard, IosPage, iosColors } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
};

const primaryActionSx = {
  bgcolor: "#B54708",
  color: "#fff",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  "&:hover": { bgcolor: "#953A05", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "rgba(181,71,8,0.18)", color: "rgba(15,15,18,0.44)" },
};

const secondaryActionSx = {
  bgcolor: "rgba(255,106,0,0.07)",
  color: "#8F3700",
  borderColor: "rgba(181,71,8,0.38)",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  "&:hover": { bgcolor: "rgba(255,106,0,0.12)", borderColor: "rgba(181,71,8,0.52)" },
};

const iconActionSx = {
  bgcolor: "rgba(255,106,0,0.07)",
  color: "#8F3700",
  border: "1px solid rgba(181,71,8,0.24)",
  "&:hover": { bgcolor: "rgba(255,106,0,0.12)" },
};

const dangerIconSx = {
  bgcolor: "rgba(215,38,30,0.07)",
  color: "#A51812",
  border: "1px solid rgba(215,38,30,0.24)",
  "&:hover": { bgcolor: "rgba(215,38,30,0.12)" },
};

const stickyFooterSx = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 20,
  px: 1,
  pt: 0.8,
  pb: "calc(env(safe-area-inset-bottom) + 0.8rem)",
  bgcolor: "rgba(247,248,250,0.96)",
  borderTop: `1px solid ${iosColors.line}`,
  backdropFilter: "blur(14px)",
};

export default function WorkOrderDetailMobile() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [editOpen, setEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState({
    buildFor: "stock",
    status: "draft",
    priority: "normal",
    customerRef: "",
    dueDate: "",
    notes: "",
  });
  const [editItemOpen, setEditItemOpen] = useState(false);
  const [editItemCtx, setEditItemCtx] = useState({
    itemId: null,
    qty: "",
    notes: "",
    inv: null,
    buildSheetId: "",
  });
  const [addOpen, setAddOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [invSearch, setInvSearch] = useState("");
  const [selectedInv, setSelectedInv] = useState(null);
  const [addQty, setAddQty] = useState("1");
  const [addNotes, setAddNotes] = useState("");
  const [selectedBuildSheetId, setSelectedBuildSheetId] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["m-work-order", documentId],
    queryFn: async () => {
      return get(`/api/work-orders/${documentId}`, {
        populate: {
          items: { populate: { inventoryItem: true, buildSheet: true } },
          finishedGood: true,
          units: true,
          documents: true,
        },
      });
    },
    enabled: !!documentId,
    staleTime: 5_000,
  });

  const wo = useMemo(() => (data?.data ? normalize(data.data) : null), [data]);
  const items = useMemo(() => {
    const raw = wo?.items?.data || wo?.items || [];
    return raw.map(normalize);
  }, [wo]);

  const units = useMemo(() => {
    const raw = wo?.units?.data || wo?.units || [];
    return raw.map(normalize);
  }, [wo]);

  const invResults = useQuery({
    queryKey: ["m-wo-add-inv", invSearch],
    queryFn: () =>
      get("/api/inventory-items", {
        sort: "sku:asc",
        pagination: { page: 1, pageSize: 25 },
        filters: {
          ...(invSearch && {
            $or: [{ sku: { $containsi: invSearch } }, { name: { $containsi: invSearch } }],
          }),
        },
      }),
    enabled: addOpen,
    staleTime: 2_000,
  });

  const buildSheetResults = useQuery({
    queryKey: ["m-wo-add-build-sheets", selectedInv?.documentId || selectedInv?.id || null],
    queryFn: async () => {
      const invId = selectedInv?.id;
      const invDoc = selectedInv?.documentId;
      if (!invId && !invDoc) return { data: [] };
      return get("/api/build-sheets", {
        filters: {
          status: { $eq: "published" },
          finishedGood: {
            $or: [
              ...(invId ? [{ id: { $eq: invId } }] : []),
              ...(invDoc ? [{ documentId: { $eq: invDoc } }] : []),
            ],
          },
        },
        sort: "version:desc",
        pagination: { page: 1, pageSize: 20 },
      });
    },
    enabled: addOpen && !!selectedInv,
    staleTime: 10_000,
  });

  const customerResults = useQuery({
    queryKey: ["m-wo-edit-customers", customerSearch],
    queryFn: () =>
      get("/api/customers", {
        sort: "name:asc",
        pagination: { page: 1, pageSize: 20 },
        filters: customerSearch
          ? {
              $or: [
                { name: { $containsi: customerSearch } },
                { contact: { $containsi: customerSearch } },
                { accountNumber: { $containsi: customerSearch } },
              ],
            }
          : {},
      }),
    enabled: editOpen && editDraft.buildFor === "customer",
    staleTime: 3_000,
  });

  const customerRows = useMemo(() => (customerResults.data?.data || []).map(normalize), [customerResults.data]);

  const addItemMutation = useMutation({
    mutationFn: async () => {
      const invId = selectedInv?.documentId || selectedInv?.id;
      const qty = parseFloat(addQty);
      if (!invId || !Number.isFinite(qty) || qty <= 0) throw new Error("Invalid item or quantity");
      return post("/api/work-order-items", {
        data: {
          workOrder: wo?.documentId || documentId,
          inventoryItem: invId,
          quantity: qty,
          buildSheet: selectedBuildSheetId || null,
          notes: addNotes?.trim() || null,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-work-order", documentId] });
      setAddOpen(false);
      setInvSearch("");
      setSelectedInv(null);
      setAddQty("1");
      setAddNotes("");
      setSelectedBuildSheetId("");
    },
  });

  useEffect(() => {
    if (!wo) return;
    const currentCustomerRef = String(wo.customerRef || "");
    const buildFor = currentCustomerRef && currentCustomerRef.toLowerCase() !== "stock" ? "customer" : "stock";
    setEditDraft({
      buildFor,
      status: wo.status || "draft",
      priority: wo.priority || "normal",
      customerRef: buildFor === "customer" ? currentCustomerRef : "Stock",
      dueDate: wo.dueDate ? String(wo.dueDate).slice(0, 10) : "",
      notes: wo.notes || "",
    });
    setCustomerSearch(buildFor === "customer" ? currentCustomerRef : "");
  }, [wo, editOpen]);

  const updateWorkOrderMutation = useMutation({
    mutationFn: (payload) => put(`/api/work-orders/${wo?.documentId || documentId}`, { data: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-work-order", documentId] });
      setEditOpen(false);
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, payload }) => put(`/api/work-order-items/${itemId}`, { data: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-work-order", documentId] });
      setEditItemOpen(false);
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: (itemId) => del(`/api/work-order-items/${itemId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-work-order", documentId] });
    },
  });

  const editBuildSheetResults = useQuery({
    queryKey: ["m-wo-edit-build-sheets", editItemCtx?.inv?.documentId || editItemCtx?.inv?.id || null],
    queryFn: async () => {
      const invId = editItemCtx?.inv?.id;
      const invDoc = editItemCtx?.inv?.documentId;
      if (!invId && !invDoc) return { data: [] };
      return get("/api/build-sheets", {
        filters: {
          status: { $eq: "published" },
          finishedGood: {
            $or: [
              ...(invId ? [{ id: { $eq: invId } }] : []),
              ...(invDoc ? [{ documentId: { $eq: invDoc } }] : []),
            ],
          },
        },
        sort: "version:desc",
        pagination: { page: 1, pageSize: 20 },
      });
    },
    enabled: editItemOpen && !!editItemCtx?.inv,
    staleTime: 10_000,
  });

  if (isLoading) {
    return (
      <IosPage>
        <LinearProgress />
      </IosPage>
    );
  }
  if (error || !wo) {
    return (
      <IosPage>
        <Typography variant="subtitle1" fontWeight={900}>
          Work order not found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Please go back and try again.
        </Typography>
      </IosPage>
    );
  }

  return (
    <IosPage>
      <MobileTopBar
        title="Work Order"
        subtitle="Back to list"
        onBack={() => navigate("/m/work-orders")}
        right={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <StatusChip value={wo.status || "draft"} />
            <IconButton aria-label="Edit work order" size="small" onClick={() => setEditOpen(true)} sx={iconActionSx}>
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Stack>
        }
      />

      <Stack spacing={0.75} sx={{ mb: 1.25 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.75}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={900}>
              {wo.workOrderNumber || "Work Order"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {wo.customerRef || "—"}
            </Typography>
          </Box>
          {/* status shown in top bar */}
        </Stack>

        <InfoCard
          title="Summary"
          rows={[
            { label: "Priority", value: String(wo.priority || "normal") },
            { label: "Due", value: fmtDate(wo.dueDate) },
            { label: "Items", value: items.length },
            { label: "Units", value: units.length },
          ]}
          footer={
            <Typography variant="caption" color="text.secondary">
              Tap the edit icon to update status, priority, due date, or notes.
            </Typography>
          }
        />
      </Stack>

      <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <span>Line items</span>
          <IconButton aria-label="Add line item" size="small" onClick={() => setAddOpen(true)} sx={iconActionSx}>
            <Icon fontSize="small">add</Icon>
          </IconButton>
        </Stack>
      </Typography>

      <Stack spacing={0.75}>
        {items.map((it) => {
          const inv = it?.inventoryItem?.data ? normalize(it.inventoryItem.data) : it?.inventoryItem ? normalize(it.inventoryItem) : null;
          const bs = it?.buildSheet?.data ? normalize(it.buildSheet.data) : it?.buildSheet ? normalize(it.buildSheet) : null;
          const qty = Number(it.quantity ?? 0);
          const invId = inv?.documentId || inv?.id || null;
          const itemId = it.documentId || it.id;

          return (
            <IosCard key={it.documentId || it.id} sx={{ minHeight: 0 }}>
                <Stack spacing={0.5}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.75}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={900}>
                        {inv?.sku || inv?.name || "Item"}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip size="small" label={`Qty ${Number.isFinite(qty) ? qty : 0}`} />
                      <IconButton
                        size="small"
                        aria-label="Edit line item"
                        onClick={() => {
                          setEditItemCtx({
                            itemId,
                            qty: String(it.quantity ?? ""),
                            notes: String(it.notes || ""),
                            inv,
                            buildSheetId: String(bs?.documentId || bs?.id || ""),
                          });
                          setEditItemOpen(true);
                        }}
                        sx={iconActionSx}
                      >
                        <Icon fontSize="small">tune</Icon>
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Delete line item"
                        disabled={deleteItemMutation.isPending}
                        onClick={() => {
                          const ok = window.confirm("Delete this line item?");
                          if (!ok) return;
                          deleteItemMutation.mutate(itemId);
                        }}
                        sx={dangerIconSx}
                      >
                        <Icon fontSize="small">delete</Icon>
                      </IconButton>
                    </Stack>
                  </Stack>

                  <CardActionArea
                    onClick={() => {
                      if (invId) navigate(`/m/inventory/${invId}`);
                    }}
                    sx={{ borderRadius: 1, px: 0.5, py: 0.25 }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block">
                      {inv?.name || "—"}
                    </Typography>
                    {bs ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Build sheet: {bs.name || bs.version || bs.documentId || bs.id}
                      </Typography>
                    ) : null}
                  </CardActionArea>
                </Stack>
            </IosCard>
          );
        })}

        {!items.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No line items yet.
          </Typography>
        ) : null}
      </Stack>

      {wo.notes ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>
            Notes
          </Typography>
          <IosCard>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {wo.notes}
              </Typography>
          </IosCard>
        </>
      ) : null}

      {/* Edit Work Order */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullScreen>
        <Box sx={{ px: 1, pt: "env(safe-area-inset-top)", pb: 10, bgcolor: iosColors.canvas, minHeight: "100dvh" }}>
          <MobileTopBar title="Edit Work Order" subtitle={wo.workOrderNumber || ""} onBack={() => setEditOpen(false)} />

          <Stack spacing={0.75} sx={{ mt: 1 }}>
            <IosCard>
              <Stack spacing={0.9}>
                <Box>
                  <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>
                    Build For
                  </Typography>
                  <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, lineHeight: 1.18 }}>
                    {editDraft.buildFor === "customer" ? editDraft.customerRef || "Select customer" : "Stock"}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75}>
                  {[
                    { key: "stock", label: "Stock", icon: "inventory_2", detail: "Internal build" },
                    { key: "customer", label: "Customer", icon: "person_search", detail: "R&R or customer job" },
                  ].map((option) => {
                    const selected = editDraft.buildFor === option.key;
                    return (
                      <ButtonBase
                        key={option.key}
                        onClick={() => {
                          setEditDraft((p) => ({
                            ...p,
                            buildFor: option.key,
                            customerRef: option.key === "stock" ? "Stock" : "",
                          }));
                          if (option.key === "stock") setCustomerSearch("");
                        }}
                        sx={{
                          flex: 1,
                          minHeight: 86,
                          borderRadius: 1,
                          border: `1px solid ${selected ? (option.key === "stock" ? "rgba(181,71,8,0.58)" : "rgba(20,122,92,0.58)") : iosColors.line}`,
                          bgcolor: selected
                            ? option.key === "stock"
                              ? "rgba(255,106,0,0.08)"
                              : "rgba(20,122,92,0.07)"
                            : "#fff",
                          color: selected && option.key === "customer" ? iosColors.green : selected ? "#8F3700" : iosColors.ink,
                          p: 1,
                          textAlign: "left",
                        }}
                      >
                        <Stack spacing={0.45} sx={{ width: "100%", minWidth: 0 }}>
                          <Icon sx={{ fontSize: 22 }}>{option.icon}</Icon>
                          <Typography sx={{ fontSize: 14, fontWeight: 900, lineHeight: 1.1 }}>{option.label}</Typography>
                          <Typography sx={{ color: iosColors.muted, fontSize: 11.75, lineHeight: 1.2 }}>{option.detail}</Typography>
                        </Stack>
                      </ButtonBase>
                    );
                  })}
                </Stack>
              </Stack>
            </IosCard>

            {editDraft.buildFor === "customer" ? (
              <IosCard>
                <Stack spacing={0.8}>
                  <TextField
                    label="Search customers"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    size="small"
                    placeholder="Name, contact, or account #"
                    fullWidth
                  />
                  {customerResults.isFetching ? <LinearProgress /> : null}
                  <Stack spacing={0.6}>
                    {customerRows.map((customer) => {
                      const selected = customer.name && customer.name === editDraft.customerRef;
                      return (
                        <ButtonBase
                          key={customer.documentId || customer.id}
                          onClick={() => {
                            setEditDraft((p) => ({ ...p, customerRef: customer.name || "" }));
                            setCustomerSearch(customer.name || "");
                          }}
                          sx={{
                            width: "100%",
                            textAlign: "left",
                            borderRadius: 1,
                            border: `1px solid ${selected ? iosColors.green : iosColors.line}`,
                            bgcolor: selected ? "rgba(20,122,92,0.07)" : "#fff",
                            p: 1,
                          }}
                        >
                          <Stack direction="row" spacing={0.8} alignItems="center" sx={{ width: "100%", minWidth: 0 }}>
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1,
                                bgcolor: selected ? "rgba(20,122,92,0.12)" : "rgba(255,106,0,0.08)",
                                color: selected ? iosColors.green : "#8F3700",
                                display: "grid",
                                placeItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Icon sx={{ fontSize: 20 }}>{selected ? "check" : "person"}</Icon>
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ color: iosColors.ink, fontSize: 14.5, fontWeight: 900, lineHeight: 1.15 }}>
                                {customer.name || "Customer"}
                              </Typography>
                              <Typography sx={{ color: iosColors.muted, fontSize: 12.25, lineHeight: 1.25 }}>
                                {[customer.accountNumber ? `Acct ${customer.accountNumber}` : "", customer.contact || ""].filter(Boolean).join(" • ") || "No account details"}
                              </Typography>
                            </Box>
                          </Stack>
                        </ButtonBase>
                      );
                    })}
                    {!customerRows.length && !customerResults.isFetching ? (
                      <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>
                        No customers found.
                      </Typography>
                    ) : null}
                  </Stack>
                </Stack>
              </IosCard>
            ) : null}

            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={editDraft.status}
                onChange={(e) => setEditDraft((p) => ({ ...p, status: e.target.value }))}
              >
                {["draft", "active", "on_hold", "complete", "shipped"].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                label="Priority"
                value={editDraft.priority}
                onChange={(e) => setEditDraft((p) => ({ ...p, priority: e.target.value }))}
              >
                {["low", "normal", "high", "urgent"].map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Due date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={editDraft.dueDate}
              onChange={(e) => setEditDraft((p) => ({ ...p, dueDate: e.target.value }))}
            />

            <TextField
              label="Notes"
              multiline
              minRows={4}
              value={editDraft.notes}
              onChange={(e) => setEditDraft((p) => ({ ...p, notes: e.target.value }))}
            />
          </Stack>
          <Box sx={stickyFooterSx}>
            {updateWorkOrderMutation.isPending ? <LinearProgress sx={{ mb: 0.75 }} /> : null}
            <Stack direction="row" spacing={0.75}>
              <Button variant="outlined" fullWidth onClick={() => setEditOpen(false)} sx={secondaryActionSx}>
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                disabled={updateWorkOrderMutation.isPending || (editDraft.buildFor === "customer" && !editDraft.customerRef)}
                sx={primaryActionSx}
                onClick={() => {
                  updateWorkOrderMutation.mutate({
                    status: editDraft.status,
                    priority: editDraft.priority,
                    customerRef: editDraft.buildFor === "stock" ? "Stock" : editDraft.customerRef || null,
                    dueDate: editDraft.dueDate || null,
                    notes: editDraft.notes || null,
                  });
                }}
              >
                Save
              </Button>
            </Stack>
          </Box>
        </Box>
      </Dialog>

      {/* Edit Line Item */}
      <Dialog open={editItemOpen} onClose={() => setEditItemOpen(false)} fullScreen>
        <Box sx={{ px: 1, pt: "env(safe-area-inset-top)", pb: 10, bgcolor: iosColors.canvas, minHeight: "100dvh" }}>
          <MobileTopBar
            title="Edit line item"
            subtitle={editItemCtx?.inv?.sku || editItemCtx?.inv?.name || ""}
            onBack={() => setEditItemOpen(false)}
          />

          <Stack spacing={0.75} sx={{ mt: 1 }}>
            <TextField
              label="Quantity"
              type="number"
              value={editItemCtx.qty}
              onChange={(e) => setEditItemCtx((p) => ({ ...p, qty: e.target.value }))}
            />

            <FormControl fullWidth size="small" disabled={!editItemCtx?.inv}>
              <InputLabel>Build sheet</InputLabel>
              <Select
                label="Build sheet"
                value={editItemCtx.buildSheetId}
                onChange={(e) => setEditItemCtx((p) => ({ ...p, buildSheetId: e.target.value }))}
              >
                <MenuItem value="">None</MenuItem>
                {(editBuildSheetResults.data?.data || []).map(normalize).map((bs) => (
                  <MenuItem key={bs.documentId || bs.id} value={String(bs.documentId || bs.id)}>
                    {bs.name || "Build sheet"} {bs.version ? `v${bs.version}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Notes"
              multiline
              minRows={4}
              value={editItemCtx.notes}
              onChange={(e) => setEditItemCtx((p) => ({ ...p, notes: e.target.value }))}
            />
          </Stack>
          <Box sx={stickyFooterSx}>
            {updateItemMutation.isPending ? <LinearProgress sx={{ mb: 0.75 }} /> : null}
            <Stack direction="row" spacing={0.75}>
              <Button variant="outlined" fullWidth onClick={() => setEditItemOpen(false)} sx={secondaryActionSx}>
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                disabled={updateItemMutation.isPending || !editItemCtx.itemId}
                sx={primaryActionSx}
                onClick={() => {
                  const q = parseFloat(editItemCtx.qty);
                  updateItemMutation.mutate({
                    itemId: editItemCtx.itemId,
                    payload: {
                      quantity: Number.isFinite(q) ? q : 0,
                      notes: editItemCtx.notes?.trim() || null,
                      buildSheet: editItemCtx.buildSheetId || null,
                    },
                  });
                }}
              >
                Save
              </Button>
            </Stack>
          </Box>
        </Box>
      </Dialog>

      {/* Add Line Item */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullScreen>
        <Box sx={{ px: 1, pt: "env(safe-area-inset-top)", pb: 10, bgcolor: iosColors.canvas, minHeight: "100dvh" }}>
          <MobileTopBar title="Add line item" subtitle={wo.workOrderNumber || ""} onBack={() => setAddOpen(false)} />

          <Stack spacing={0.75} sx={{ mt: 1 }}>
            <TextField
              label="Search inventory (SKU or name)"
              value={invSearch}
              onChange={(e) => setInvSearch(e.target.value)}
            />

            <Stack spacing={0.75}>
              {(invResults.data?.data || [])
                .map(normalize)
                .slice(0, 8)
                .map((inv) => {
                  const isSel = (selectedInv?.documentId || selectedInv?.id) === (inv.documentId || inv.id);
                  return (
                    <IosCard
                      key={inv.documentId || inv.id}
                      sx={{
                        minHeight: 0,
                        p: 0,
                        borderColor: isSel ? iosColors.green : iosColors.line,
                        bgcolor: isSel ? "rgba(20,122,92,0.07)" : "#fff",
                      }}
                    >
                      <CardActionArea
                        onClick={() => {
                          setSelectedInv(inv);
                          setSelectedBuildSheetId("");
                        }}
                        sx={{ p: 1 }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={900}>
                              {inv.sku || inv.name || "Item"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {inv.name || "—"}
                            </Typography>
                          </Box>
                          {isSel ? <Chip size="small" label="Selected" sx={{ bgcolor: iosColors.green, color: "#fff", fontWeight: 900 }} /> : null}
                        </Stack>
                      </CardActionArea>
                    </IosCard>
                  );
                })}
            </Stack>

            {selectedInv ? (
              <>
                <Divider />
                <TextField
                  label="Quantity"
                  type="number"
                  value={addQty}
                  onChange={(e) => setAddQty(e.target.value)}
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Build sheet (optional)</InputLabel>
                  <Select
                    label="Build sheet (optional)"
                    value={selectedBuildSheetId}
                    onChange={(e) => setSelectedBuildSheetId(e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {(buildSheetResults.data?.data || []).map(normalize).map((bs) => (
                      <MenuItem key={bs.documentId || bs.id} value={bs.documentId || bs.id}>
                        {bs.name || "Build sheet"} {bs.version ? `v${bs.version}` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Notes (optional)"
                  multiline
                  minRows={3}
                  value={addNotes}
                  onChange={(e) => setAddNotes(e.target.value)}
                />

              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Select an inventory item to continue.
              </Typography>
            )}
          </Stack>
          <Box sx={stickyFooterSx}>
            {addItemMutation.isPending ? <LinearProgress sx={{ mb: 0.75 }} /> : null}
            <Stack direction="row" spacing={0.75}>
              <Button variant="outlined" fullWidth onClick={() => setAddOpen(false)} sx={secondaryActionSx}>
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                disabled={addItemMutation.isPending || !selectedInv}
                onClick={() => addItemMutation.mutate()}
                sx={primaryActionSx}
              >
                {addItemMutation.isPending ? "Adding..." : "Add item"}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Dialog>
    </IosPage>
  );
}
